import './ConsumptionDetailsBar.less';

import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip as AntdTooltip, Form, Select, Space, Tooltip, Typography, message } from 'antd';
import { ChartType, MeasureType } from '../ConsumptionBody';
import ConsumptionFilter from '../ConsumptionFilter/ConsumptionFilter';
import { downloadConsumptionToCSV, getClustersConsumption } from 'utils/cluster';
import { ConsumptionFiltersOptions } from 'components/Consumption/Consumption';
const { Title } = Typography;

interface ConsumptionDetailsBarProps {
  setChartType: (value: ChartType) => void;
  filteredClusters?: ReturnType<typeof getClustersConsumption>;
  clustersFormatted?: ReturnType<typeof getClustersConsumption>;
  isLoading: boolean;
  setFilters: React.Dispatch<React.SetStateAction<ConsumptionFiltersOptions>>;
  filters: ConsumptionFiltersOptions;
  chartType: ChartType;
  setMeasureType: React.Dispatch<React.SetStateAction<MeasureType>>;
  measureType: MeasureType;
  selectedDataType: 'compute' | 'storage' | 'classA' | 'classB' | 'downloads' | 'uploads';
  setSelectedDataType: React.Dispatch<
    React.SetStateAction<'compute' | 'storage' | 'classA' | 'classB' | 'downloads' | 'uploads'>
  >;
  chartRef: React.RefObject<{ getEChartInstance: () => echarts.ECharts | null }>;
  tableRef: React.RefObject<HTMLDivElement>;
}

function ConsumptionDetailsBar({
  filters,
  setFilters,
  filteredClusters,
  clustersFormatted,
  isLoading,
  selectedDataType,
  setSelectedDataType,
  chartRef,
  tableRef,
}: ConsumptionDetailsBarProps) {
  const dataTypeOptions = [
    {
      value: 'compute',
      label: 'Compute Usage (IPUs)',
      tooltip: 'IPUs (Incorta Power Units) are a measure of compute usage',
    },
    {
      value: 'storage',
      label: 'Historical Bucket Storage',
      tooltip: 'Storage is measured in GB/TB and represents your data storage usage',
    },
    {
      value: 'classA',
      label: 'Class-A Operations',
      tooltip: 'Class-A represents Write-only access operations performed on your bucket.',
    },
    {
      value: 'classB',
      label: 'Class-B Operations',
      tooltip: 'Class-B represents read-only access operations performed on your bucket.',
    },
    {
      value: 'downloads',
      label: 'Downloads (Size)',
      tooltip: 'Downloads represent the total size of data downloaded from your bucket.',
    },
    {
      value: 'uploads',
      label: 'Uploads (Size)',
      tooltip: 'Uploads represent the total size of data uploaded to your bucket.',
    },
  ];

  const [exportType, setExportType] = React.useState<string | undefined>(undefined);
  const exportOptions = [
    {
      value: 'csv',
      label: 'Export as CSV',
      tooltip: 'CSV downloads table only',
    },
    {
      value: 'png',
      label: 'Export as PNG',
      tooltip: 'PNG downloads graph only',
    },
    {
      value: 'pdf',
      label: 'Export as PDF',
      tooltip: 'PDF includes both table and graph',
    },
  ];

  React.useEffect(() => {
    if (!exportType) return;
    const doExport = async () => {
      if (exportType === 'csv') {
        if (filteredClusters) {
          downloadConsumptionToCSV(filteredClusters);
        }
      } else if (exportType === 'png') {
        if (chartRef?.current?.getEChartInstance) {
          const echart = chartRef.current.getEChartInstance();
          if (echart) {
            const url = echart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
            const link = document.createElement('a');
            link.href = url;
            link.download = 'consumption-chart.png';
            link.click();
            message.success('Chart downloaded as PNG');
          } else {
            message.error('Chart is not available');
          }
        } else {
          message.error('Chart is not available');
        }
      } else if (exportType === 'pdf') {
        try {
          const jsPDF = (await import('jspdf')).default;
          const html2canvas = (await import('html2canvas')).default;
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          if (chartRef?.current?.getEChartInstance) {
            const echart = chartRef.current.getEChartInstance();
            if (echart) {
              const chartImg = echart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
              doc.addImage(chartImg, 'PNG', 40, 40, 700, 250);
            }
          }
          if (tableRef?.current) {
            const tableCanvas = await html2canvas(tableRef.current, {
              backgroundColor: '#fff',
              scale: 2,
            });
            const tableImg = tableCanvas.toDataURL('image/png');
            doc.addImage(tableImg, 'PNG', 40, 320, 700, 200);
          }
          doc.save('consumption.pdf');
          message.success('PDF downloaded');
        } catch (err) {
          message.error('Failed to export PDF');
        }
      }
      setExportType(undefined);
    };
    doExport();
  }, [exportType, filteredClusters, chartRef, tableRef]);

  return (
    <>
      <div className="ConsumptionDetailsBar">
        <Title level={4} className="ant-typography ConsumptionDetailsBar__title truncate">
          IPU Consumption Details
        </Title>

        <div className="ConsumptionDetailsBar__actions">
          <Space>
            <Select
              value={exportType}
              onChange={setExportType}
              placeholder="Export"
              style={{ width: 110, color: exportType }}
              dropdownMatchSelectWidth={false}
              className={!exportType ? 'ConsumptionDetailsBar__export-placeholder' : ''}
            >
              {exportOptions.map((opt) => (
                <Select.Option value={opt.value} key={opt.value}>
                  <AntdTooltip title={opt.tooltip} placement="right">
                    {opt.label}
                  </AntdTooltip>
                </Select.Option>
              ))}
            </Select>
          </Space>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Form layout="vertical" style={{ marginBottom: 0 }}>
          <Form.Item label="Select Data Type" style={{ marginBottom: 0 }}>
            <Select value={selectedDataType} style={{ width: 220 }} onChange={setSelectedDataType}>
              {dataTypeOptions.map(({ value, label, tooltip }) => (
                <Select.Option value={value} key={value}>
                  <span
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                  >
                    <span>{label}</span>
                    <Tooltip title={tooltip} placement="right">
                      <InfoCircleOutlined style={{ color: '#b0b0b0', marginLeft: 8 }} />
                    </Tooltip>
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        <div style={{ flex: 1 }} />
      </div>
      <ConsumptionFilter
        filters={filters}
        setFilters={setFilters}
        isLoading={isLoading}
        clustersFormatted={clustersFormatted}
      />
    </>
  );
}

export default ConsumptionDetailsBar;
